'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Image from 'next/image';
import { useInView } from 'react-intersection-observer';
import { getGalleryImages } from '@/utils/imageSettings';
import styles from './Gallery.module.css';
import { trackEvent } from '@/lib/analytics/firebaseAnalytics';
import { AnalyticsEvents } from '@/lib/analytics/events';
import { ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';

interface GalleryContent {
  header: string;
  text: string;
}

type GalleryTypeId = 'back' | 'leg' | 'handstand';

interface GallerySlide {
  imageIndex: number;
  typeId: GalleryTypeId;
}

/** gallery1–6 in CRM order: 1&4 back, 2&5 leg, 3&6 handstands */
const SLIDE_COUNT = 6;

const GALLERY_SLIDES: GallerySlide[] = Array.from({ length: SLIDE_COUNT }, (_, imageIndex) => ({
  imageIndex,
  typeId: (['back', 'leg', 'handstand'] as const)[imageIndex % 3],
}));

const GALLERY_CONTENT: Record<GalleryTypeId, GalleryContent> = {
  back: {
    header: 'Back Flexibility Training',
    text: 'Focused on improving mobility and strength through the spine, shoulders, and hip flexors.\n\nSessions include varied stretching methods designed to build strength and control at end range, all while integrating breath control and activation of deep core muscles, inner thighs, pelvic floor, and neck stabilizers, to create support and reduce stress throughout the practice.',
  },
  leg: {
    header: 'Leg Flexibility Training',
    text: 'Focused on improving mobility and strength through the hips, hamstrings, hip flexors, and ankles.\n\nSessions include active and progressive stretching methods designed to build strength and control at end range, improve alignment, and support safe progress toward full splits.',
  },
  handstand: {
    header: 'Handstands Training',
    text: 'Training designed to develop control in inverted positions through specific strength work, flexibility, technique, and learning to manage fear.',
  },
};

/** Time for one slide width to pass — controls continuous scroll speed */
const SLIDE_DURATION_MS = 6831;
const RESUME_AUTO_PLAY_MS = 6000;
const DESKTOP_SLIDES_PER_VIEW = 4;

function normalizeScrollPosition(position: number): number {
  const wrapped = position % SLIDE_COUNT;
  return wrapped < 0 ? wrapped + SLIDE_COUNT : wrapped;
}

const Gallery = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [leadingIndex, setLeadingIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);

  const trackRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const dragOffsetRef = useRef(0);
  const scrollPositionRef = useRef(0);
  const leadingIndexRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef<number | null>(null);
  const autoPlayPausedRef = useRef(false);
  const isDraggingRef = useRef(false);
  const slidesPerViewRef = useRef(1);

  const activeSlide = GALLERY_SLIDES[leadingIndex] ?? GALLERY_SLIDES[0];
  const activeContent = GALLERY_CONTENT[activeSlide.typeId];

  const clearResumeTimer = useCallback(() => {
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  }, []);

  const getSlideWidthPx = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return 0;
    return viewport.clientWidth / slidesPerViewRef.current;
  }, []);

  const applyTransform = useCallback((dragPx = 0) => {
    const track = trackRef.current;
    const slideWidth = getSlideWidthPx();
    if (!track || slideWidth <= 0) return;

    const offsetPx = scrollPositionRef.current * slideWidth - dragPx;
    track.style.transform = `translate3d(-${offsetPx}px, 0, 0)`;
  }, [getSlideWidthPx]);

  const syncLeadingIndex = useCallback(() => {
    const nextLeading = Math.floor(normalizeScrollPosition(scrollPositionRef.current)) % SLIDE_COUNT;
    if (nextLeading !== leadingIndexRef.current) {
      leadingIndexRef.current = nextLeading;
      setLeadingIndex(nextLeading);
    }
  }, []);

  const snapToSlideStart = useCallback((position: number) => {
    return normalizeScrollPosition(Math.round(position));
  }, []);

  const pauseAutoPlay = useCallback(() => {
    autoPlayPausedRef.current = true;
    clearResumeTimer();
    resumeTimerRef.current = setTimeout(() => {
      autoPlayPausedRef.current = false;
    }, RESUME_AUTO_PLAY_MS);
  }, [clearResumeTimer]);

  const stepCarousel = useCallback(
    (direction: -1 | 1) => {
      const current = scrollPositionRef.current;
      const target =
        direction > 0
          ? Math.floor(current + 1e-6) + 1
          : Math.ceil(current - 1e-6) - 1;

      scrollPositionRef.current = normalizeScrollPosition(target);
      syncLeadingIndex();
      applyTransform();
      pauseAutoPlay();
    },
    [applyTransform, pauseAutoPlay, syncLeadingIndex]
  );

  const togglePlayPause = useCallback(() => {
    clearResumeTimer();
    autoPlayPausedRef.current = false;
    if (isPlaying) {
      scrollPositionRef.current = snapToSlideStart(scrollPositionRef.current);
      syncLeadingIndex();
      applyTransform();
    }
    setIsPlaying((prev) => !prev);
  }, [applyTransform, clearResumeTimer, isPlaying, snapToSlideStart, syncLeadingIndex]);

  const stopControlPropagation = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const commitDrag = useCallback(() => {
    const slideWidth = getSlideWidthPx();
    if (slideWidth <= 0) return;

    scrollPositionRef.current = normalizeScrollPosition(
      scrollPositionRef.current - dragOffsetRef.current / slideWidth
    );
    scrollPositionRef.current = snapToSlideStart(scrollPositionRef.current);
    dragOffsetRef.current = 0;
    syncLeadingIndex();
    applyTransform();
  }, [applyTransform, getSlideWidthPx, snapToSlideStart, syncLeadingIndex]);

  useEffect(() => {
    if (inView && !hasTrackedView) {
      trackEvent(AnalyticsEvents.VIEW_GALLERY_SECTION);
      setHasTrackedView(true);
    }
  }, [inView, hasTrackedView]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const syncLayout = () => {
      const mobile = mediaQuery.matches;
      setIsMobile(mobile);
      slidesPerViewRef.current = mobile ? 1 : DESKTOP_SLIDES_PER_VIEW;
      const viewport = viewportRef.current;
      if (viewport) {
        const slideWidth = viewport.clientWidth / slidesPerViewRef.current;
        viewport.style.setProperty('--slide-width', `${slideWidth}px`);
      }
      applyTransform(isDraggingRef.current ? dragOffsetRef.current : 0);
    };

    const syncMotion = () => {
      const reduced = motionQuery.matches;
      setPrefersReducedMotion(reduced);
      if (reduced) {
        setIsPlaying(false);
      }
    };

    syncLayout();
    syncMotion();

    mediaQuery.addEventListener('change', syncLayout);
    motionQuery.addEventListener('change', syncMotion);
    window.addEventListener('resize', syncLayout);

    return () => {
      mediaQuery.removeEventListener('change', syncLayout);
      motionQuery.removeEventListener('change', syncMotion);
      window.removeEventListener('resize', syncLayout);
    };
  }, [applyTransform]);

  useEffect(() => {
    if (!imagesLoaded) return;
    const viewport = viewportRef.current;
    if (viewport) {
      const slideWidth = viewport.clientWidth / slidesPerViewRef.current;
      viewport.style.setProperty('--slide-width', `${slideWidth}px`);
    }
    applyTransform();
    syncLeadingIndex();
  }, [imagesLoaded, applyTransform, syncLeadingIndex, isMobile]);

  useEffect(() => {
    const loadImages = async () => {
      try {
        const imgs = await getGalleryImages();
        setGalleryImages(imgs);
        setImagesLoaded(true);
      } catch (error) {
        console.error('Error loading gallery images:', error);
      }
    };
    loadImages();
  }, []);

  useEffect(() => {
    if (!imagesLoaded || !inView || prefersReducedMotion || !isPlaying) {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastFrameTimeRef.current = null;
      return;
    }

    const tick = (time: number) => {
      if (lastFrameTimeRef.current !== null && !autoPlayPausedRef.current && !isDraggingRef.current) {
        const delta = time - lastFrameTimeRef.current;
        scrollPositionRef.current += delta / SLIDE_DURATION_MS;

        if (scrollPositionRef.current >= SLIDE_COUNT) {
          scrollPositionRef.current -= SLIDE_COUNT;
        }

        applyTransform();
        syncLeadingIndex();
      }

      lastFrameTimeRef.current = time;
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastFrameTimeRef.current = null;
    };
  }, [applyTransform, imagesLoaded, inView, isPlaying, prefersReducedMotion, syncLeadingIndex]);

  useEffect(
    () => () => {
      clearResumeTimer();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    },
    [clearResumeTimer]
  );

  const handleTouchStart = (event: React.TouchEvent) => {
    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    isDraggingRef.current = true;
    setIsDragging(true);
    dragOffsetRef.current = 0;
    pauseAutoPlay();
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    if (!touchStartRef.current) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;

    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      return;
    }

    event.preventDefault();
    dragOffsetRef.current = deltaX;
    applyTransform(deltaX);
  };

  const handleTouchEnd = () => {
    if (!touchStartRef.current) {
      isDraggingRef.current = false;
      setIsDragging(false);
      dragOffsetRef.current = 0;
      return;
    }

    commitDrag();
    touchStartRef.current = null;
    isDraggingRef.current = false;
    setIsDragging(false);
  };

  const handlePointerDown = (event: React.PointerEvent) => {
    if (event.pointerType === 'touch') return;
    touchStartRef.current = { x: event.clientX, y: event.clientY };
    isDraggingRef.current = true;
    setIsDragging(true);
    dragOffsetRef.current = 0;
    pauseAutoPlay();
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent) => {
    if (!touchStartRef.current || event.pointerType === 'touch') return;
    dragOffsetRef.current = event.clientX - touchStartRef.current.x;
    applyTransform(dragOffsetRef.current);
  };

  const handlePointerUp = (event: React.PointerEvent) => {
    if (event.pointerType === 'touch') return;

    commitDrag();
    touchStartRef.current = null;
    isDraggingRef.current = false;
    setIsDragging(false);
  };

  const renderSlide = (slide: GallerySlide, keySuffix: string, slideIndex: number) => {
    const image = galleryImages[slide.imageIndex];
    if (!image) return null;
    const content = GALLERY_CONTENT[slide.typeId];

    return (
      <article key={`${slide.imageIndex}-${keySuffix}`} className={styles.carouselSlide}>
        <div className={styles.slideImageWrap}>
          <Image
            src={image}
            alt={content.header}
            fill
            sizes={
              isMobile
                ? '100vw'
                : `(max-width: 1200px) 25vw, ${Math.round(100 / DESKTOP_SLIDES_PER_VIEW)}vw`
            }
            style={{ objectFit: 'cover' }}
            priority={slideIndex < DESKTOP_SLIDES_PER_VIEW}
          />
          {slide.typeId === 'handstand' && (
            <div className={styles.upcomingBadge}>
              <span>Upcoming</span>
            </div>
          )}
        </div>
      </article>
    );
  };

  return (
    <section className={styles.gallery} id="gallery" ref={ref}>
      {imagesLoaded && galleryImages.length > 0 && (
        <div className={`${styles.carouselRoot} ${inView ? styles.carouselVisible : ''}`}>
          <div
            ref={viewportRef}
            className={styles.carouselViewport}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <div
              ref={trackRef}
              className={`${styles.carouselTrack} ${isDragging ? styles.carouselTrackDragging : ''}`}
            >
              {GALLERY_SLIDES.map((slide, index) => renderSlide(slide, 'a', index))}
              {GALLERY_SLIDES.map((slide, index) => renderSlide(slide, 'b', index + SLIDE_COUNT))}
            </div>

            <div className={styles.carouselControls}>
              <button
                type="button"
                className={styles.carouselControlBtn}
                onClick={() => stepCarousel(-1)}
                onPointerDown={stopControlPropagation}
                onTouchStart={stopControlPropagation}
                aria-label="Previous image"
              >
                <ChevronLeft size={18} strokeWidth={2} />
              </button>
              <button
                type="button"
                className={styles.carouselControlBtn}
                onClick={togglePlayPause}
                onPointerDown={stopControlPropagation}
                onTouchStart={stopControlPropagation}
                aria-label={isPlaying ? 'Pause auto-scroll' : 'Play auto-scroll'}
              >
                {isPlaying ? <Pause size={15} strokeWidth={2} /> : <Play size={15} strokeWidth={2} />}
              </button>
              <button
                type="button"
                className={styles.carouselControlBtn}
                onClick={() => stepCarousel(1)}
                onPointerDown={stopControlPropagation}
                onTouchStart={stopControlPropagation}
                aria-label="Next image"
              >
                <ChevronRight size={18} strokeWidth={2} />
              </button>
            </div>
          </div>

          <div className={styles.textPanel} aria-live="polite">
            <div key={activeSlide.typeId} className={`${styles.textContent} ${styles.textContentAnimate}`}>
              <h3 className={styles.textHeader}>{activeContent.header}</h3>
              <p className={styles.textBody}>{activeContent.text}</p>
              {activeSlide.typeId === 'handstand' && (
                <span className={styles.upcomingChip}>Upcoming</span>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
